import React, { useCallback, useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { approvePayout, getAllAdminUsers, rejectPayout } from "services";

import {
  formatNumber,
  getAdminVerificationYearlyDataYear,
  isSingleFieldOutcomes,
  useAdminUserContext,
  useCancelAsyncOnUnmount,
  useGetAllCarbonProgramOutcomesAdmin,
  useGetFieldVerifications,
} from "utils";

import { GET_CARBON_PROGRAM_OUTCOMES_ADMIN, GET_VERIFICATION_EDITING_REQUESTS } from "consts";

import { PRACTICE_SLUG } from "models";

import {
  Avatar,
  Button,
  Col,
  Drawer,
  Form,
  InsightCard,
  Modal,
  Radio,
  Row,
  Table,
  TablePanel,
  Tag,
  TextArea,
  Typography,
  Message as message,
} from "components";

import { PayoutExtraInfo } from "pages/admin/verifications/components/PayoutExtraInfo";
import { PayoutStickyButtons } from "pages/admin/verifications/components/PayoutStickyButtons";

const allowedCreditDelta = 0.001;

const sumCredits = (carbonProgramOutcomes) => {
  const verificationYear = getAdminVerificationYearlyDataYear();
  // We need to filter for all the data which include fieldOutcomes (not fully sure why, following the pattern in the user app here)
  const filteredFieldOutcomes = Object.values(carbonProgramOutcomes ?? {}).filter(isSingleFieldOutcomes);

  const points = filteredFieldOutcomes.reduce((sum, outcome) => {
    let newSum = sum;
    Object.values(PRACTICE_SLUG).forEach((practiceSlug) => {
      const yearData = outcome?.fieldOutcomes?.[practiceSlug]?.years?.[verificationYear];
      if (!yearData || !("points" in yearData)) return;

      const { points: yearPoints } = yearData;
      if (!yearPoints) return;

      newSum += yearPoints.removals.avg + yearPoints.avoidances.avg + yearPoints.reductions.avg;
    });

    return newSum;
  }, 0);

  return points / 1000;
};

export const PayoutVerificationDrawer = ({
  farmId,
  visible,
  onComplete,
  onClose,
  farmIsVerified,
  verificationSummary,
  payout,
  credits,
  preVerificationPayout,
  preVerificationCredits,
  editingRequestCounts,
}) => {
  const signal = useCancelAsyncOnUnmount()();
  const queryClient = useQueryClient();

  const { adminVerificationPercentages } = verificationSummary;

  const { data: fieldData } = useGetFieldVerifications({ farmId });
  const { data: carbonProgramOutcomes, progress: recalculationProgress } = useGetAllCarbonProgramOutcomesAdmin(
    fieldData?.fields.map((f) => f.id) ?? [],
    true,
    visible
  );

  const recalculatedCredits = sumCredits(carbonProgramOutcomes);

  const { user } = useAdminUserContext();

  const [loading, setLoading] = useState(true);
  const [adminUsers, setAdminUsers] = useState(null);
  const [saving, setSaving] = useState(false);
  const [trustStatus, setTrustStatus] = useState(false);
  const [adminNotes, setAdminNotes] = useState(false);
  const [showRejectModalConfirm, setShowRejectModalConfirm] = useState(false);
  const [showApproveModalConfirm, setShowApproveModalConfirm] = useState(false);

  useEffect(() => {
    if (!visible) {
      queryClient.invalidateQueries({ queryKey: [GET_CARBON_PROGRAM_OUTCOMES_ADMIN] });
    }
  }, [visible, queryClient]);

  const openEditingRequestsCount = editingRequestCounts.open.fields + editingRequestCounts.open.crops;
  const unreviewedEditingRequestsCount = editingRequestCounts.completed.fields + editingRequestCounts.completed.crops;

  const totalInvolvedVerifiers = adminVerificationPercentages.length;
  const tableData = adminVerificationPercentages.map(({ adminId, total }) => {
    if (adminUsers) {
      const admin = adminUsers.find((admin) => admin.id === adminId);
      return {
        key: adminId,
        image: <TablePanel image={<Avatar size={"xs"} name={admin.name} />} headline={<Typography variant="paragraph-m">{admin.name}</Typography>} />,
        name: `${formatNumber({ number: total, decimalPlaces: 1 })}%`,
      };
    }
    return {};
  });

  const showModal = (type) => {
    if (type === "approve") {
      setShowApproveModalConfirm(true);
    } else if (type === "reject") {
      setShowRejectModalConfirm(true);
    }
  };

  const handleApprovePayout = () => {
    setSaving(true);
    approvePayout({
      farmId,
      trustStatus,
      adminNotes,
      verifier: user.id,
      signal,
    })
      .then(() => {
        message({ content: "Payout approved", icon: "tick" });
        queryClient.invalidateQueries({ queryKey: [GET_VERIFICATION_EDITING_REQUESTS, farmId, true] });
      })
      .finally(() => {
        setSaving(false);
        onComplete();
        setShowApproveModalConfirm(false);
      });
  };

  const handleRejectPayout = () => {
    setSaving(true);
    rejectPayout({
      farmId,
      trustStatus,
      adminNotes,
      verifier: user.id,
      signal,
    })
      .then(() => {
        message({ content: "Payout rejected", icon: "tick" });
      })
      .finally(() => {
        setSaving(false);
        onComplete();
        setShowRejectModalConfirm(false);
      });
  };

  const getModalData = (type) => {
    if (type === "reject") {
      return {
        okText: "Reject payout",
        title: "Confirm payout Rejection?",
        description:
          "After rejecting payout farmer will receive notification that verifications are completed and payout was rejected. You will no longer be able to change any verification statuses nor farmer data.",
        onOk: () => handleRejectPayout(),
        onCancel: () => setShowRejectModalConfirm(false),
        showModal: showRejectModalConfirm,
      };
    }
    return {
      okText: "Approve payout",
      title: "Confirm payout approval?",
      description:
        "After approving payout farmer will receive notification that verifications are completed and payout was successfully approved. You will no longer be able to change any verification statuses nor farmer data.",
      onOk: () => handleApprovePayout(),
      onCancel: () => setShowApproveModalConfirm(false),
      showModal: showApproveModalConfirm,
    };
  };

  const loadAdminUsers = useCallback(() => {
    getAllAdminUsers({
      signal,
    }).then((res) => {
      setLoading(false);
      setAdminUsers(res);
    });
  }, [signal]);

  useEffect(() => {
    loadAdminUsers();
  }, [loadAdminUsers]);

  const creditsNotMatching = credits === undefined || credits === null || Math.abs(credits - recalculatedCredits) > allowedCreditDelta;

  const getButtonText = () => {
    if (!farmIsVerified) return "Reject all";
    if (recalculationProgress !== 100) return "Safety credit recalculation";
    if (creditsNotMatching) return "Approve anyway";
    return "Approve payout";
  };

  return (
    <Drawer visible={visible} onClose={onClose} title={"Payout Verifications"}>
      <div className="margin-bottom-200">
        <Tag text="verify payout" colour="blue" className="margin-bottom-50" />
        <Typography variant="h1" className="margin-bottom-100">
          Review details below and decide if payout should be approved or rejected
        </Typography>
        <Typography variant="paragraph-m">
          After this step farmer will be notified about verification results. You will no longer be able to change any verification statuses nor farmer data.
          Make sure to inform farmer about payout changes before completing payout verification.
        </Typography>
      </div>

      <Row className="margin-bottom-100">
        <Col>
          <Typography variant="h3">Verifiers</Typography>
        </Col>
        <Col>
          <Table
            data={tableData}
            loading={loading}
            hideHeader={true}
            className="verifications-tasks-table"
            pagination={{
              total: totalInvolvedVerifiers,
              defaultPageSize: totalInvolvedVerifiers,
              pageSize: totalInvolvedVerifiers,
            }}
          />
        </Col>
      </Row>

      <Row className="margin-bottom-100">
        <Col>
          <Typography variant="h3">Editing requests</Typography>
        </Col>
        <Col sm={12}>
          <InsightCard label="open (will be deleted)" status={openEditingRequestsCount > 0 ? "alert" : ""}>
            {openEditingRequestsCount}
          </InsightCard>
        </Col>
        <Col sm={12}>
          <InsightCard label="to review" status={unreviewedEditingRequestsCount > 0 ? "alert" : ""}>
            {unreviewedEditingRequestsCount}
          </InsightCard>
        </Col>
      </Row>

      <Row className="margin-bottom-100">
        <Col className={"margin-bottom-100"}>
          <Typography variant="h3">How trustworthy do you rate this farmer?</Typography>
        </Col>
        <Col>
          <Form name="post">
            <Form.Item name="defaultTask">
              <Radio.Group onChange={(v) => setTrustStatus(v.target.value)}>
                <Row>
                  <Col>
                    <Radio value={"very"}>Very (verify less next time)</Radio>
                  </Col>
                  <Col>
                    <Radio value={"normal"}>Normal (verify same next time)</Radio>
                  </Col>
                  <Col>
                    <Radio value={"questionable"}>Questionable (verify more next time)</Radio>
                  </Col>
                </Row>
              </Radio.Group>
            </Form.Item>
            <Form.Item>
              <div className="padding-top-200">
                <Row>
                  <Col>
                    <Typography variant="h3">Verification notes</Typography>
                  </Col>
                  <Col>
                    <TextArea
                      label="Write your note...."
                      rules={[{ required: false }]}
                      autoSize={{ minRows: 4 }}
                      onChange={(v) => setAdminNotes(v.target.value)}
                    />
                  </Col>
                </Row>
              </div>
            </Form.Item>
          </Form>
        </Col>
      </Row>

      <PayoutStickyButtons
        absolute={true}
        extra={
          <>
            <PayoutExtraInfo
              farmIsVerified={farmIsVerified}
              preVerificationCredits={preVerificationCredits}
              preVerificationPayout={preVerificationPayout}
              credits={credits}
              payout={payout}
            />
            {recalculationProgress === 100 && creditsNotMatching && (
              <Typography variant="paragraph-m" color="red-500">
                The in realtime calculated credits ({formatNumber({ number: recalculatedCredits, decimalPlaces: 2 })}) do not match the stored credits.
                Something is off, please contact product/engineering.
              </Typography>
            )}
          </>
        }
        primary={
          <Button
            type={farmIsVerified && creditsNotMatching ? "danger" : "primary"}
            onClick={() => (farmIsVerified ? showModal("approve") : showModal("reject"))}
            className="margin-left-100"
            size="large"
            block
            loading={recalculationProgress !== 100}
          >
            {getButtonText()}
          </Button>
        }
        secondary={
          farmIsVerified ? (
            <Button type={"secondary"} onClick={() => showModal("reject")} size="large" block disabled={false}>
              Reject All
            </Button>
          ) : null
        }
      />
      <Modal cancelText={"Cancel"} {...getModalData(showApproveModalConfirm ? "approve" : "reject")} loading={saving} />
    </Drawer>
  );
};
